import { errorFilledToastOptions, ibanLengths } from "../../../../constants"
import { lang } from "../../../../constants/languages"
import { PaymentMethod } from "../../../../types"
import { toast } from "react-toastify"
import { CountryType } from "./countryConfig"

const checkIbanValidity = (iban: string) => {
  const cleanedIban = iban.replace(/\s+/g, "")

  if (cleanedIban.length < 2) return lang.account.payment_methods.payment_form.error.short_iban

  const firstTwo = cleanedIban.slice(0, 2)

  const allowedLength = ibanLengths[firstTwo]

  if (!allowedLength) return lang.account.payment_methods.payment_form.error.country_code_invalid

  if (allowedLength > cleanedIban.length) return lang.account.payment_methods.payment_form.error.short_iban

  if (allowedLength < cleanedIban.length) return lang.account.payment_methods.payment_form.error.long_iban

  const rearranged = cleanedIban.slice(4) + cleanedIban.slice(0, 4)

  let numericIban = ""
  for (const char of rearranged) {
    const code = char.charCodeAt(0)
    numericIban += code >= 65 && code <= 90 ? code - 55 : char
  }
  let remainder = numericIban
  while (remainder.length > 2) {
    const block = remainder.slice(0, 9)
    remainder = (parseInt(block, 10) % 97) + remainder.slice(block.length)
  }

  if (!(parseInt(remainder, 10) % 97 === 1)) return lang.account.payment_methods.payment_form.error.invalid_iban

  return null
}

const paymentMethodValidator = (formValues: PaymentMethod, countryType: CountryType) => {
  const errors: any = {}
  const err = lang.account.payment_methods.payment_form.error

  if (!formValues.accountHolderName) {
    errors.accountHolderName = err.account_holder_name_blank
    toast.error(err.account_holder_name_blank, errorFilledToastOptions)
  }

  if (!formValues.currency) {
    errors.currency = err.currency_select
    toast.error(err.currency_select, errorFilledToastOptions)
  }

  switch (countryType) {
    case "us": {
      const routingClean = (formValues.bankCode || "").replace(/\D/g, "")
      if (routingClean.length !== 9) {
        errors.bankCode = err.routing_number_invalid
        toast.error(err.routing_number_invalid, errorFilledToastOptions)
      }
      if (!formValues.accountNumber) {
        errors.accountNumber = err.account_number_required
        toast.error(err.account_number_required, errorFilledToastOptions)
      }
      break
    }

    case "ng": {
      const nuban = (formValues.accountNumber || "").replace(/\D/g, "")
      if (nuban.length !== 10) {
        errors.accountNumber = err.nuban_invalid
        toast.error(err.nuban_invalid, errorFilledToastOptions)
      }
      if (!formValues.bankName) {
        errors.bankName = err.select_bank
        toast.error(err.select_bank, errorFilledToastOptions)
      }
      break
    }

    case "za": {
      const branchCode = (formValues.bankCode || "").replace(/\D/g, "")
      if (branchCode.length !== 6) {
        errors.bankCode = err.branch_code_invalid
        toast.error(err.branch_code_invalid, errorFilledToastOptions)
      }
      if (!formValues.bankName) {
        errors.bankName = err.select_bank
        toast.error(err.select_bank, errorFilledToastOptions)
      }
      if (!formValues.accountNumber) {
        errors.accountNumber = err.account_number_required
        toast.error(err.account_number_required, errorFilledToastOptions)
      }
      if (!formValues.accountType) {
        errors.accountType = err.account_type_required
        toast.error(err.account_type_required, errorFilledToastOptions)
      }
      break
    }

    case "au": {
      const bsb = (formValues.bankCode || "").replace(/\D/g, "")
      if (bsb.length !== 6) {
        errors.bankCode = err.bsb_invalid
        toast.error(err.bsb_invalid, errorFilledToastOptions)
      }
      if (!formValues.accountNumber) {
        errors.accountNumber = err.account_number_required
        toast.error(err.account_number_required, errorFilledToastOptions)
      }
      break
    }

    case "ca": {
      const cleanBankCode = (formValues.bankCode || "").replace(/\D/g, "")
      if (!cleanBankCode || cleanBankCode.length !== 8) {
        errors.bankCode = err.transit_institution_invalid
        toast.error(err.transit_institution_invalid, errorFilledToastOptions)
      }
      if (!formValues.accountNumber) {
        errors.accountNumber = err.account_number_required
        toast.error(err.account_number_required, errorFilledToastOptions)
      }
      break
    }

    case "in": {
      const ifsc = (formValues.bankCode || "").toUpperCase()
      if (!ifsc || !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) {
        errors.bankCode = err.ifsc_invalid
        toast.error(err.ifsc_invalid, errorFilledToastOptions)
      }
      if (!formValues.accountNumber) {
        errors.accountNumber = err.account_number_required
        toast.error(err.account_number_required, errorFilledToastOptions)
      }
      break
    }

    case "sg": {
      const cleanBankCode = (formValues.bankCode || "").replace(/\D/g, "")
      if (!cleanBankCode || cleanBankCode.length !== 7) {
        errors.bankCode = err.bank_branch_invalid
        toast.error(err.bank_branch_invalid, errorFilledToastOptions)
      }
      if (!formValues.accountNumber) {
        errors.accountNumber = err.account_number_required
        toast.error(err.account_number_required, errorFilledToastOptions)
      }
      break
    }

    case "br": {
      const cleanBankCode = (formValues.bankCode || "").replace(/\D/g, "")
      if (!cleanBankCode || cleanBankCode.length < 7 || cleanBankCode.length > 8) {
        errors.bankCode = err.bank_code_agency_invalid
        toast.error(err.bank_code_agency_invalid, errorFilledToastOptions)
      }
      if (!formValues.accountNumber) {
        errors.accountNumber = err.account_number_required
        toast.error(err.account_number_required, errorFilledToastOptions)
      }
      if (!formValues.accountType) {
        errors.accountType = err.account_type_required
        toast.error(err.account_type_required, errorFilledToastOptions)
      }
      const taxId = (formValues.taxId || "").replace(/\D/g, "")
      if (!taxId || (taxId.length !== 11 && taxId.length !== 14)) {
        errors.taxId = err.cpf_cnpj_invalid
        toast.error(err.cpf_cnpj_invalid, errorFilledToastOptions)
      }
      break
    }

    case "ph": {
      if (!formValues.bankName) {
        errors.bankName = err.select_bank
        toast.error(err.select_bank, errorFilledToastOptions)
      }
      if (!formValues.accountNumber) {
        errors.accountNumber = err.account_number_required
        toast.error(err.account_number_required, errorFilledToastOptions)
      }
      break
    }

    case "nz": {
      const cleanAccount = (formValues.accountNumber || "").replace(/\D/g, "")
      if (!cleanAccount || cleanAccount.length < 15 || cleanAccount.length > 16) {
        errors.accountNumber = err.nz_account_invalid
        toast.error(err.nz_account_invalid, errorFilledToastOptions)
      }
      if (!formValues.bankName) {
        errors.bankName = err.bank_name_blank
        toast.error(err.bank_name_blank, errorFilledToastOptions)
      }
      const swift = (formValues.swiftCode || "").toUpperCase().trim()
      if (!swift) {
        errors.swiftCode = err.swift_bic_required
        toast.error(err.swift_bic_required, errorFilledToastOptions)
      } else if (!/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(swift)) {
        errors.swiftCode = err.swift_bic_invalid
        toast.error(err.swift_bic_invalid, errorFilledToastOptions)
      }
      break
    }

    case "uk": {
      const sortCode = (formValues.bankCode || "").replace(/\D/g, "")
      if (sortCode.length !== 6) {
        errors.bankCode = err.sort_code_invalid
        toast.error(err.sort_code_invalid, errorFilledToastOptions)
      }
      if (!formValues.accountNumber || formValues.accountNumber.length !== 8) {
        errors.accountNumber = err.account_number_8_digits
        toast.error(err.account_number_8_digits, errorFilledToastOptions)
      }
      if (!formValues.bankName) {
        errors.bankName = err.bank_name_blank
        toast.error(err.bank_name_blank, errorFilledToastOptions)
      }
      break
    }

    case "tr": {
      if (!formValues.iban) {
        errors.iban = err.iban_blank
        toast.error(err.iban_blank, errorFilledToastOptions)
      } else {
        const errorMessage = checkIbanValidity(formValues.iban)
        if (errorMessage !== null) {
          errors.iban = errorMessage
          toast.error(errorMessage, errorFilledToastOptions)
        }
      }
      if (!formValues.bankName) {
        errors.bankName = err.bank_name_blank
        toast.error(err.bank_name_blank, errorFilledToastOptions)
      }
      break
    }

    case "eu":
    default: {
      if (!formValues.iban) {
        errors.iban = err.iban_blank
        toast.error(err.iban_blank, errorFilledToastOptions)
      } else {
        const errorMessage = checkIbanValidity(formValues.iban)
        if (errorMessage !== null) {
          errors.iban = errorMessage
          toast.error(errorMessage, errorFilledToastOptions)
        }
      }
      if (!formValues.bankName) {
        errors.bankName = err.bank_name_blank
        toast.error(err.bank_name_blank, errorFilledToastOptions)
      }
      break
    }
  }

  return errors
}

export default paymentMethodValidator
